import React, { useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Shared fullscreen catch viewer. Open when `index` is a valid number, closed when null.
// Navigates through `items` (list of catches). Used by the map markers and the catch lists.
const CatchLightbox = ({ items, index, onClose, onIndexChange }) => {
  const touchStartX = useRef(null);

  const open = index != null && index >= 0;
  const total = items.length;
  const current = open ? items[index] : null;

  const goNext = () => onIndexChange((index + 1) % total);
  const goPrev = () => onIndexChange((index - 1 + total) % total);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) diff > 0 ? goNext() : goPrev();
    touchStartX.current = null;
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen PaperProps={{ sx: { bgcolor: '#000' } }}>
      {/* Close */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'fixed',
          top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
          right: 8,
          zIndex: 10,
          color: '#fff',
          bgcolor: 'rgba(0,0,0,0.45)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          '&:focus': { outline: 'none' },
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Counter */}
      {total > 1 && (
        <Box
          sx={{
            position: 'fixed',
            top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            bgcolor: 'rgba(0,0,0,0.45)',
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
          }}
        >
          <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>
            {index + 1} / {total}
          </Typography>
        </Box>
      )}

      {/* Image */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', userSelect: 'none' }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {current?.imageUrl ? (
          <Box
            component="img"
            src={current.imageUrl}
            alt={current.species}
            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ color: 'white', fontSize: 56, fontWeight: 700, lineHeight: 1 }}>
              {(current?.species || '?')[0].toUpperCase()}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
              Brak zdjęcia
            </Typography>
          </Box>
        )}
      </Box>

      {/* Info overlay */}
      {current && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 72px)',
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        >
          <Box sx={{ bgcolor: 'rgba(0,0,0,0.55)', borderRadius: 2, px: 2, py: 0.75, textAlign: 'center' }}>
            <Typography sx={{ color: '#fff', fontWeight: 700, textTransform: 'capitalize', fontSize: 15 }}>
              {current.species}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
              {new Date(current.caughtAt).toLocaleDateString('pl-PL')}
              {current.weightKg != null ? ` · ${current.weightKg} kg` : ''}
              {current.lengthCm != null ? ` · ${current.lengthCm} cm` : ''}
              {current.username ? ` · @${current.username}` : ''}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Prev / Next */}
      {total > 1 && (
        <>
          <IconButton
            onClick={goPrev}
            sx={{
              position: 'fixed',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.45)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              '&:focus': { outline: 'none' },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            onClick={goNext}
            sx={{
              position: 'fixed',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.45)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              '&:focus': { outline: 'none' },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </>
      )}
    </Dialog>
  );
};

export default CatchLightbox;
